import './users-table.css';

import { EnvelopeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ReactNode } from 'react';

import { Badge } from '@/components/core/Badge';
import { IconButton } from '@/components/core/IconButton';
import { Loader } from '@/components/core/Loader';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/core/Table';
import { formatDate } from '@/services/dateFormat';
import { cn } from '@/services/utils';

import { canManageUser, filterLabel } from '../user-management.format';
import { PendingInvitation, RestrictionFilterOption, RoleDefinition, UserAccount } from '../user-management.types';
import { RoleBadges } from './RoleBadges';
import { UserAvatar } from './UserAvatar';

interface UsersTableProps {
    users: UserAccount[];
    pendingInvitations: PendingInvitation[];
    isLoading: boolean;
    roleMetadataByName: Record<string, RoleDefinition>;
    filters: RestrictionFilterOption[];
    displayRoles: (user: UserAccount) => string[];
    labels: {
        user: string;
        roles: string;
        restrictionFilter: string;
        restrictionQuery: string;
        actions: string;
        empty: string;
        systemUser: string;
        managingUser: string;
        lastLoggedIn: (lastLogin: string) => string;
        editUser: string;
        deleteUser: string;
        resend: string;
        deleteInvitation: string;
    };
    onEdit: (user: UserAccount) => void;
    onDeleteUser: (user: UserAccount) => void;
    onResend: (invitation: PendingInvitation) => void;
    onDeleteInvitation: (invitation: PendingInvitation) => void;
}

const DATA_ROW_CLASS = 'hover:tw-bg-fill-secondary tw-transition-colors tw-duration-100 tw-ease-in-out';

function rowAccent(user: UserAccount): string {
    if (user.systemAccount || user.managingAccount) {
        return 'user-row-accent-system';
    }
    return 'user-row-accent-active';
}

export function UsersTable({
    users,
    pendingInvitations,
    isLoading,
    roleMetadataByName,
    filters,
    displayRoles,
    labels,
    onEdit,
    onDeleteUser,
    onResend,
    onDeleteInvitation,
}: UsersTableProps) {
    const activeUsers = users.filter((user) => user.active);
    const showEmpty = !isLoading && users.length < 1;

    return (
        <div
            className="tw-w-full tw-max-w-full tw-overflow-x-auto tw-border border-table"
            onMouseDown={(event) => {
                const target = event.target as HTMLElement;
                if (!target.closest('td')) {
                    return;
                }
                if (target.closest('button, a, input, textarea, select')) {
                    return;
                }
                event.preventDefault();
            }}
        >
            <Table id="user-management-table" className="lg:tw-text-t-base">
                <TableHeader>
                    <TableRow className="table-header-row !tw-border-b-0">
                        <TableHead leftPinned data-column-id="user" className="tw-min-w-[180px]">{labels.user}</TableHead>
                        <TableHead>{labels.roles}</TableHead>
                        <TableHead>{labels.restrictionFilter}</TableHead>
                        <TableHead>{labels.restrictionQuery}</TableHead>
                        <TableHead pinned data-column-id="actions" className="tw-whitespace-nowrap">{labels.actions}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading && (
                        <TableRow>
                            <TableCell hasPadding colSpan={5} className="tw-h-24 tw-text-center">
                                <span className="tw-flex tw-items-center tw-justify-center">
                                    <Loader />
                                </span>
                            </TableCell>
                        </TableRow>
                    )}
                    {showEmpty && (
                        <TableRow>
                            <TableCell hasPadding colSpan={5} className="tw-text-center tw-text-t-sm lg:tw-text-t-base">
                                {labels.empty}
                            </TableCell>
                        </TableRow>
                    )}
                    {!isLoading && users.length > 0 && activeUsers.map((user) => {
                        const restriction = filterLabel(filters, user.restrictionFilter);
                        return (
                            <TableRow
                                key={user.id}
                                className={cn(`user-row-${user.userId}`, DATA_ROW_CLASS, rowAccent(user))}
                            >
                                <TableCell hasPadding leftPinned data-column-id="user" className="tw-min-w-[180px]">
                                    <div className="tw-flex tw-items-start tw-gap-3 tw-min-w-0 tw-m-1">
                                        <UserAvatar imageUrl={user.imageUrl} name={user.displayName} size="list" />
                                        <div className="tw-min-w-0">
                                            <span className="tw-block tw-text-t-sm lg:tw-text-t-base tw-text-t-primary">
                                                {user.displayName}
                                            </span>
                                            {user.email && (
                                                <a
                                                    className="tw-block tw-text-t-xs lg:tw-text-t-sm tw-text-primary"
                                                    href={`mailto:${user.email}`}
                                                >
                                                    {user.email}
                                                </a>
                                            )}
                                            {user.systemAccount && (
                                                <span className="tw-block tw-text-t-xs lg:tw-text-t-sm tw-text-error">
                                                    {labels.systemUser}
                                                </span>
                                            )}
                                            {user.managingAccount && (
                                                <span className="tw-block tw-text-t-xs lg:tw-text-t-sm tw-text-error">
                                                    {labels.managingUser}
                                                </span>
                                            )}
                                            <span className="tw-block tw-text-t-xs lg:tw-text-t-sm tw-text-t-secondary">
                                                {labels.lastLoggedIn(formatDate(user.lastLogin, 'medium'))}
                                            </span>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell hasPadding>
                                    <RoleBadges roles={displayRoles(user)} roleMetadataByName={roleMetadataByName} />
                                </TableCell>
                                <TableCell hasPadding>
                                    {restriction && (
                                        <span className="tw-inline-flex tw-m-1">
                                            <Badge variant="neutral">{restriction}</Badge>
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell hasPadding className="tw-min-w-0 tw-break-words">
                                    {user.restrictionQuery && (
                                        <span className="tw-m-1 tw-inline-block">{user.restrictionQuery}</span>
                                    )}
                                </TableCell>
                                <TableCell hasPadding pinned data-column-id="actions" className="tw-whitespace-nowrap">
                                    {canManageUser(user) && (
                                        <RowActions>
                                            <IconButton
                                                icon={PencilIcon}
                                                size="md"
                                                className="btn-edit tw-icon-primary"
                                                title={labels.editUser}
                                                onClick={() => onEdit(user)}
                                            />
                                            <IconButton
                                                icon={TrashIcon}
                                                size="md"
                                                className="tw-icon-primary"
                                                title={labels.deleteUser}
                                                onClick={() => onDeleteUser(user)}
                                            />
                                        </RowActions>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    {pendingInvitations.map((invitation) => {
                        const restriction = filterLabel(filters, invitation.restrictionFilter);
                        return (
                            <TableRow
                                key={`invitation-${invitation.id}`}
                                className={cn('user-row-accent-pending table-row-pending', DATA_ROW_CLASS)}
                            >
                                <TableCell hasPadding leftPinned data-column-id="user" className="tw-min-w-[180px]">
                                    <div className="tw-flex tw-items-start tw-gap-3 tw-min-w-0 tw-m-1">
                                        <UserAvatar size="list" />
                                        <div className="tw-min-w-0">
                                            {invitation.invitationState && (
                                                <Badge variant="warning">{invitation.invitationState}</Badge>
                                            )}
                                            {invitation.invitedEmailAddress && (
                                                <a
                                                    className="tw-mt-1 tw-block tw-text-t-xs lg:tw-text-t-sm tw-text-primary"
                                                    href={`mailto:${invitation.invitedEmailAddress}`}
                                                >
                                                    {invitation.invitedEmailAddress}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell hasPadding>
                                    <RoleBadges roles={invitation.roles ?? []} roleMetadataByName={roleMetadataByName} />
                                </TableCell>
                                <TableCell hasPadding>
                                    {restriction && (
                                        <span className="tw-inline-flex tw-m-1">
                                            <Badge variant="neutral">{restriction}</Badge>
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell hasPadding className="tw-min-w-0 tw-break-words">
                                    {invitation.restrictionQuery && (
                                        <span className="tw-m-1 tw-inline-block">{invitation.restrictionQuery}</span>
                                    )}
                                </TableCell>
                                <TableCell hasPadding pinned data-column-id="actions" className="tw-whitespace-nowrap">
                                    <RowActions>
                                        {invitation.invitationState !== 'ACCEPTED' && (
                                            <IconButton
                                                icon={EnvelopeIcon}
                                                size="md"
                                                className="tw-icon-primary"
                                                title={labels.resend}
                                                onClick={() => onResend(invitation)}
                                            />
                                        )}
                                        <IconButton
                                            icon={TrashIcon}
                                            size="md"
                                            className="tw-icon-primary"
                                            title={labels.deleteInvitation}
                                            onClick={() => onDeleteInvitation(invitation)}
                                        />
                                    </RowActions>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}

function RowActions({ children }: { children: ReactNode }) {
    return (
        <div className="actions tw-flex tw-h-10 tw-max-h-10 tw-items-center tw-justify-end tw-space-x-1">
            {children}
        </div>
    );
}
